// Raum nach Wänden (15.10.2026, Betreiber): feste Reihenfolge ab der Zimmertür – gegenüberliegende Wand, rechte Wand,
// linke Wand, dann vom anderen Ende des Raums die Wand mit der Tür. Lange Wände in überlappenden Teilen. Kleine Räume
// (Bad, Abstellraum, Balkon) in der Kurzform mit zwei Fotos; umschaltbar. „Raum hat eine Ecke oder Nische?“ fragt erst
// auf Antippen nach der Form und ergänzt dann eine Karte für die Ecke bzw. Nische. Jede Karte zeigt ein kleines
// Piktogramm: Raum von oben, Tür unten, die zu fotografierende Wand hervorgehoben.

import { useEffect, useState } from "react";
import { CABINET_ROOM_TYPES, ROOM_SHAPE_LABEL, ROOM_SMALL_STEPS, ROOM_WALL_STEPS, defaultPhotoMode, type PhotoStep, type RoomPhotoMode, type RoomShape } from "@umzugsscan/shared";
import { useDraft, useStore } from "../hooks.ts";
import type { Photo, Room } from "../zustand.ts";
import { FotoReihe } from "./Galerie.tsx";
import { Kamera } from "./Kamera.tsx";

/** Aufnahmeart eines Raums: gespeichert, sonst ältere Aufnahme mit Rundum-Fotos (null), sonst nach Raumtyp. */
export function photoModeOf(room: Room, photos: Photo[]): RoomPhotoMode | null {
  if (room.photoMode) return room.photoMode;
  if (photos.some((p) => p.roomId === room.id && p.step === "rundum")) return null;
  return defaultPhotoMode(room.type);
}

/** Pflichtfotos je Aufnahmeart (Ecke/Nische nur, wenn der Kunde sie angegeben hat). */
export function requiredSteps(mode: RoomPhotoMode, shape: RoomShape | null | undefined): PhotoStep[] {
  if (mode === "kurz") return [...ROOM_SMALL_STEPS];
  return shape === "l_foermig" || shape === "nische" ? [...ROOM_WALL_STEPS, "wand_nische"] : [...ROOM_WALL_STEPS];
}

export const STEP_TITLE: Partial<Record<PhotoStep, string>> = {
  wand_gegenueber: "Gegenüberliegende Wand",
  wand_rechts: "Rechte Wand",
  wand_links: "Linke Wand",
  wand_tuer: "Wand mit der Tür",
  wand_nische: "Ecke oder Nische",
  klein_rein: "Aus der Tür in den Raum",
  klein_zurueck: "Zurück zur Tür",
};

const TEXT: Partial<Record<PhotoStep, string>> = {
  wand_gegenueber: "Stellen Sie sich in die Tür und fotografieren Sie die Wand gegenüber. Ist sie zu lang für ein Foto, in Teilen von links nach rechts, jedes Foto etwa ein Drittel überlappend.",
  wand_rechts: "Weiter in der Tür stehen, nach rechts drehen: die rechte Wand. Lange Wände in überlappenden Teilen, von der Tür weg.",
  wand_links: "Jetzt nach links drehen: die linke Wand, genauso.",
  wand_tuer: "Gehen Sie ans andere Ende des Raums, drehen Sie sich um und fotografieren Sie die Wand mit der Tür, durch die Sie hereingekommen sind.",
  wand_nische: "Fotografieren Sie den Teil um die Ecke bzw. die Nische: erst hinein, dann jede Wand darin.",
  klein_rein: "Stellen Sie sich in die Tür, Handy quer, Kamera auf 0,5×: ein Foto in den Raum hinein.",
  klein_zurueck: "Stellen Sie sich in die hinterste Ecke und fotografieren Sie zurück zur Tür.",
};

/** Raum von oben, Tür unten. Die Wand der Karte ist hervorgehoben, der Punkt zeigt, wo der Kunde steht. */
export function WandPiktogramm(props: { step: PhotoStep }) {
  const s = props.step;
  const wand: Partial<Record<PhotoStep, string>> = {
    wand_gegenueber: "M8 8 H48",
    wand_rechts: "M48 8 V48",
    wand_links: "M8 8 V48",
    wand_tuer: "M8 48 H22 M34 48 H48",
    wand_nische: "M36 8 H48 V22",
    klein_rein: "M8 8 H48",
    klein_zurueck: "M8 48 H22 M34 48 H48",
  };
  // Standort und Blickrichtung.
  const [px, py, tx, ty] =
    s === "wand_tuer" ? [28, 14, 28, 40] : s === "klein_zurueck" ? [13, 13, 25, 40] : s === "wand_rechts" ? [28, 44, 42, 30] : s === "wand_links" ? [28, 44, 14, 30] : s === "wand_nische" ? [28, 30, 38, 16] : [28, 44, 28, 16];
  const raum = s === "wand_nische" ? "M8 8 H36 V22 H48 V48 H34 M22 48 H8 Z" : "M8 8 H48 V48 H34 M22 48 H8 Z";
  return (
    <svg className="wand-pikto" viewBox="0 0 56 56" width="56" height="56" aria-hidden="true">
      <path d={raum} fill="var(--accent-soft)" stroke="var(--muted)" strokeWidth="2" strokeLinejoin="round" />
      <path d="M22 48 Q22 38 32 38" fill="none" stroke="var(--muted)" strokeWidth="1.2" strokeDasharray="2 2" />
      <path d={wand[s] ?? ""} fill="none" stroke="var(--karton)" strokeWidth="5" strokeLinecap="round" />
      <line x1={px} y1={py} x2={tx} y2={ty} stroke="var(--accent)" strokeWidth="2" markerEnd="url(#pfeil)" />
      <circle cx={px} cy={py} r="3.5" fill="var(--accent)" />
      <defs>
        <marker id="pfeil" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
          <path d="M0 0 L10 5 L0 10 Z" fill="var(--accent)" />
        </marker>
      </defs>
    </svg>
  );
}

const iPhone = typeof navigator !== "undefined" && /iPhone|iPad/.test(navigator.userAgent);

export function RaumWaende(props: { room: Room; onBusy: (b: boolean) => void }) {
  const d = useDraft();
  const store = useStore();
  const { room } = props;
  const mode = photoModeOf(room, d.photos) ?? "waende";
  const [formFrage, setFormFrage] = useState(false);
  const shape = room.roomShape ?? null;

  // Aufnahmeart einmal am Raum festhalten (Prüfung, KI und Skizze lesen sie).
  useEffect(() => {
    if (!room.photoMode) store.saveRoom({ ...room, photoMode: mode });
  }, [room, mode, store]);

  const of = (step: PhotoStep) => d.photos.filter((p) => p.roomId === room.id && p.step === step).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const steps = requiredSteps(mode, shape);
  // Gezielt neu anfordern (15.10.2026): Eine Wand ohne Foto gilt als fehlend, sobald der Raum schon fertig war, eine
  // spätere Wand Fotos hat (Foto nachträglich gelöscht) oder der Kunde aus der Prüfung kommt.
  const missing = steps.filter(
    (st, i) => of(st).length === 0 && (room.confirmedComplete || d.nav.returnToCheck || steps.slice(i + 1).some((x) => of(x).length > 0)),
  );
  const first = missing[0];
  useEffect(() => {
    if (first && d.nav.returnToCheck) document.getElementById(`wand-${first}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first]);
  // „Schrank geöffnet fehlt“ aus der Prüfung: direkt zur Schrankkarte.
  useEffect(() => {
    if (d.nav.phase === "schrank" && d.nav.returnToCheck) document.getElementById("wand-schrank")?.scrollIntoView({ behavior: "smooth", block: "center" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const setMode = (m: RoomPhotoMode) => store.saveRoom({ ...room, photoMode: m });
  const setShape = (sh: RoomShape) => {
    store.saveRoom({ ...room, roomShape: sh });
    setFormFrage(false);
  };

  return (
    <>
      <div className="tip weitwinkel">
        <strong>Kamera auf 0,5× (Weitwinkel).</strong> In Räumen unter etwa 20 m² passt sonst kaum eine ganze Wand aufs Bild.
        {iPhone && " Fehlt der 0,5-Knopf, fotografieren Sie mit der Kamera-App und wählen hier „Aus der Galerie wählen“."}
      </div>
      {steps.map((step, i) => {
        const list = of(step);
        const fehlt = missing.includes(step);
        return (
          <section className={`card wand-karte${fehlt ? " fehlt" : ""}`} key={step} id={`wand-${step}`}>
            <div className="wand-kopf">
              <WandPiktogramm step={step} />
              <div>
                <p className="eyebrow">
                  Foto {i + 1} von {steps.length}
                  {list.length > 0 && " · ✓"}
                </p>
                <h2>{STEP_TITLE[step]}</h2>
              </div>
            </div>
            {fehlt && <p className="wand-fehlt">Fehlt noch – bitte diese Wand (neu) fotografieren.</p>}
            <p>{TEXT[step]}</p>
            <Kamera roomId={room.id} step={step} zoomHint={false} onBusy={props.onBusy} label={list.length > 0 ? "📷 Weiteren Teil fotografieren" : undefined} />
            <FotoReihe photos={list} onDelete={(p) => store.deletePhoto(p.id)} />
          </section>
        );
      })}
      {CABINET_ROOM_TYPES.includes(room.type) && (
        // Seit 30.10.2026 keine eigene Schrankfrage mehr: freiwillige Karte auf derselben Seite.
        <section className="card wand-karte" id="wand-schrank">
          <p className="eyebrow">Freiwillig{of("schrank_offen").length > 0 && " · ✓"}</p>
          <h2>Große Schränke geöffnet</h2>
          {room.cabinets === "nein" && of("schrank_offen").length === 0 ? (
            <p>
              Keine großen Schränke in diesem Raum.{" "}
              <button type="button" className="link" onClick={() => store.saveRoom({ ...room, cabinets: null })}>
                Doch
              </button>
            </p>
          ) : (
            <>
              <p>Kleiderschrank, Schrankwand oder Einbauschrank: Türen auf und ein Foto. So schätzen wir die Zahl der Kartons.</p>
              <Kamera roomId={room.id} step="schrank_offen" zoomHint={false} onBusy={props.onBusy} label={of("schrank_offen").length > 0 ? "📷 Weiteren Schrank fotografieren" : "📷 Offenen Schrank fotografieren"} />
              <FotoReihe photos={of("schrank_offen")} onDelete={(p) => store.deletePhoto(p.id)} />
              {of("schrank_offen").length === 0 && (
                <button type="button" className="link" onClick={() => store.setCabinets(room.id, "nein")}>
                  Hier gibt es keine großen Schränke
                </button>
              )}
            </>
          )}
        </section>
      )}
      <div className="wand-optionen">
        {mode === "kurz" ? (
          <button type="button" className="link" onClick={() => setMode("waende")}>
            Raum ist größer → 4 Wände fotografieren
          </button>
        ) : (
          <>
            {formFrage ? (
              <div className="formwahl" role="group" aria-label="Form des Raums">
                {(["l_foermig", "nische", "rechteckig"] as const).map((sh) => (
                  <button key={sh} type="button" className={`formknopf${shape === sh ? " on" : ""}`} aria-pressed={shape === sh} onClick={() => setShape(sh)}>
                    <RaumformPiktogramm form={sh} />
                    <span>{sh === "rechteckig" ? "Doch rechteckig" : ROOM_SHAPE_LABEL[sh]}</span>
                  </button>
                ))}
              </div>
            ) : (
              <button type="button" className="link" onClick={() => setFormFrage(true)}>
                {shape === "l_foermig" || shape === "nische" ? `Form: ${ROOM_SHAPE_LABEL[shape]} ändern` : "Raum hat eine Ecke oder Nische?"}
              </button>
            )}
            {(room.type === "bad" || room.type === "abstellraum" || room.type === "balkon") && (
              <button type="button" className="link" onClick={() => setMode("kurz")}>
                Kleiner Raum → nur 2 Fotos
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
}

function RaumformPiktogramm(props: { form: RoomShape }) {
  const d = props.form === "l_foermig" ? "M6 6 H22 V20 H34 V34 H6 Z" : props.form === "nische" ? "M6 6 H34 V34 H24 V40 H16 V34 H6 Z" : "M6 6 H34 V34 H6 Z";
  return (
    <svg viewBox="0 0 40 42" width="40" height="42" aria-hidden="true">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" />
    </svg>
  );
}
